import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Send,
  Loader2,
  Check,
  MessageSquare,
  Clock,
  CheckCircle2,
  StickyNote,
  Zap,
  UserPlus,
  Phone,
  MapPin,
  MoreHorizontal,
  Users,
  Pin,
  PinOff,
  Star,
  ChevronUp,
  Trash2,
  ArrowLeft,
  ArrowRight,
  X,
  Info,
} from "lucide-react";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { FileUpload } from "@/components/chat/FileUpload";
import { MessageAttachment } from "@/components/chat/MessageAttachment";
import { TruncatedMessage } from "@/components/chat/TruncatedMessage";
import { ScrollToBottomButton } from "@/components/chat/ScrollToBottomButton";
import { MessageStatus } from "@/components/chat/MessageStatus";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { OnlineStatus } from "@/components/chat/OnlineStatus";
import { QuickRepliesSheet } from "@/components/chat/QuickRepliesSheet";
import { MessageAvatar } from "@/components/chat/MessageAvatar";
import { SystemMessage } from "@/components/chat/SystemMessage";
import { DeletedMessage } from "@/components/chat/DeletedMessage";
import { SelectionToolbar } from "@/components/chat/SelectionToolbar";
import { SelectableMessage } from "@/components/chat/SelectableMessage";
import { DeleteSelectedDialog } from "@/components/chat/DeleteSelectedDialog";
import { useTypingIndicator } from "@/hooks/useTypingIndicator";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useMessageSelection } from "@/hooks/useMessageSelection";
import { useMessageSync, SyncMessage } from "@/hooks/useMessageSync";

import ReassignDialog from "./ReassignDialog";

interface InternalNote {
  id: string;
  conversation_id: string;
  agent_id: string;
  content: string;
  created_at: string;
}

interface QuickReply {
  id: string;
  title: string;
  content: string;
  category: string | null;
  service_id: string | null;
}

interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  address_line1: string | null;
  city: string | null;
  emirate: string | null;
  country: string | null;
  avatar_url: string | null;
}

interface ServiceInfo {
  id: string;
  name: string;
}

interface Conversation {
  id: string;
  user_id: string;
  assigned_agent_id: string | null;
  status: "open" | "pending" | "closed";
  subject: string | null;
  service: string | null;
  service_id?: string | null;
  user_profile?: UserProfile;
  is_archived?: boolean;
}

interface AgentChatViewProps {
  conversation: Conversation;
  onStatusChange: (status: "open" | "pending" | "closed") => void;
  onTakeOver?: () => void;
  onBack?: () => void;
  isMobile?: boolean;
}

export default function AgentChatView({
  conversation,
  onStatusChange,
  onTakeOver,
  onBack,
  isMobile = false,
}: AgentChatViewProps) {
  const { user, isAdmin, profile: agentProfile } = useAuth();
  const [notes, setNotes] = useState<InternalNote[]>([]);
  const [senderProfiles, setSenderProfiles] = useState<Record<string, {
    first_name: string | null;
    last_name: string | null;
    display_name: string | null;
  }>>({});
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [currentService, setCurrentService] = useState<ServiceInfo | null>(null);
  const [fullProfile, setFullProfile] = useState<UserProfile | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [showStarredPanel, setShowStarredPanel] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Message sync hook
  const { messages, setMessages, isLoading: messagesLoading, refetch } = useMessageSync({
    conversationId: conversation.id,
    userId: user?.id,
    pollingIntervalMs: 3000,
    enabled: !!conversation.id && !!user?.id,
  });

  // Message selection hook
  const {
    selectedMessageIds,
    isSelectionMode,
    toggleMessageSelection,
    selectMessage,
    clearSelection,
    isSelected,
  } = useMessageSelection();

  // Typing indicator hook
  const { typingUsers, sendTyping, stopTyping, isTyping } = useTypingIndicator(conversation.id);
  
  // Online status hook
  const clientOnlineStatus = useOnlineStatus(conversation.user_id);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const scrollToMessage = useCallback((messageId: string) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      messageElement.classList.add("ring-2", "ring-primary", "ring-offset-2");
      setTimeout(() => {
        messageElement.classList.remove("ring-2", "ring-primary", "ring-offset-2");
      }, 2000);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setShowScrollButton(distanceFromBottom > window.innerHeight);
    }
  }, []);

  // Fetch notes, quick replies, services, and profile
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      // Fetch internal notes
      const { data: notesData } = await supabase
        .from("internal_notes")
        .select("*")
        .eq("conversation_id", conversation.id)
        .order("created_at", { ascending: false });

      if (notesData) setNotes(notesData as InternalNote[]);

      // Fetch quick replies
      const { data: repliesData } = await supabase
        .from("quick_replies")
        .select("*")
        .order("title", { ascending: true });
      
      if (repliesData) {
        const filteredReplies = repliesData.filter(
          (r) => r.is_global || r.service_id === conversation.service_id
        );
        setQuickReplies(filteredReplies as QuickReply[]);
      }

      // Fetch all services
      const { data: servicesData } = await supabase
        .from("services")
        .select("id, name")
        .order("sort_order", { ascending: true });

      if (servicesData) {
        setServices(servicesData as ServiceInfo[]);
        if (conversation.service_id) {
          const current = servicesData.find((s) => s.id === conversation.service_id);
          setCurrentService(current || null);
        }
      }

      // Fetch full profile
      const { data: profileData } = await supabase
        .from("profiles")
        .select("first_name, last_name, phone, address_line1, city, emirate, country, avatar_url")
        .eq("id", conversation.user_id)
        .maybeSingle();

      if (profileData) setFullProfile(profileData);

      // Mark messages as read
      await supabase
        .from("messages")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("conversation_id", conversation.id)
        .eq("is_from_agent", false)
        .eq("is_read", false);

      await supabase
        .from("conversations")
        .update({ unread_count_agent: 0 })
        .eq("id", conversation.id);

      setLoading(false);
    };

    fetchData();
  }, [conversation.id, conversation.user_id, conversation.service_id]);

  // Fetch agent sender profiles for message attribution
  useEffect(() => {
    const fetchSenderProfiles = async () => {
      const agentSenderIds = [...new Set(
        messages
          .filter(m => m.is_from_agent && !m.is_system_message)
          .map(m => m.sender_id)
      )];
      
      if (agentSenderIds.length === 0) return;
      
      const { data } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, display_name")
        .in("id", agentSenderIds);
      
      if (data) {
        const profilesMap: Record<string, { first_name: string | null; last_name: string | null; display_name: string | null }> = {};
        data.forEach(p => {
          profilesMap[p.id] = { first_name: p.first_name, last_name: p.last_name, display_name: p.display_name };
        });
        setSenderProfiles(profilesMap);
      }
    };
    
    if (messages.length > 0) {
      fetchSenderProfiles();
    }
  }, [messages]);

  // Scroll to bottom after initial load
  useEffect(() => {
    if (!loading && !messagesLoading && messages.length > 0) {
      setTimeout(() => scrollToBottom("instant"), 100);
    }
  }, [loading, messagesLoading, scrollToBottom]);

  // Handle delete completion
  const handleDeleteComplete = useCallback((deletedIds: string[], forEveryone: boolean) => {
    if (forEveryone) {
      setMessages(prev => prev.map(m => 
        deletedIds.includes(m.id) 
          ? { ...m, is_deleted: true, deleted_at: new Date().toISOString(), deleted_by: user?.id || null } 
          : m
      ));
    } else {
      setMessages(prev => prev.filter(m => !deletedIds.includes(m.id)));
    }
    clearSelection();
  }, [user?.id, setMessages, clearSelection]);

  // Check if user can delete for everyone
  const canDeleteForEveryone = useCallback(() => {
    if (!user) return false;
    for (const id of selectedMessageIds) {
      const msg = messages.find(m => m.id === id);
      if (msg && msg.sender_id !== user.id) {
        return false;
      }
    }
    return true;
  }, [selectedMessageIds, messages, user]);

  const handleSend = async () => {
    if (!newMessage.trim() || !user || sending) return;

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage("");

    const { error } = await supabase.from("messages").insert({
      conversation_id: conversation.id,
      sender_id: user.id,
      content: messageContent,
      is_from_agent: true,
      delivered_at: new Date().toISOString(),
    });

    if (error) {
      toast.error("Failed to send message");
      setNewMessage(messageContent);
    } else {
      if (!conversation.assigned_agent_id) {
        onTakeOver?.();
      }
      setTimeout(() => scrollToBottom(), 100);
    }

    setSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !user || savingNote) return;

    setSavingNote(true);
    const { error } = await supabase.from("internal_notes").insert({
      conversation_id: conversation.id,
      agent_id: user.id,
      content: newNote.trim(),
    });

    if (error) {
      toast.error("Failed to add note");
    } else {
      toast.success("Note added");
      setNewNote("");
      const { data } = await supabase
        .from("internal_notes")
        .select("*")
        .eq("conversation_id", conversation.id)
        .order("created_at", { ascending: false });
      if (data) setNotes(data as InternalNote[]);
    }

    setSavingNote(false);
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm("Supprimer cette note ?")) return;
    
    setDeletingNoteId(noteId);
    const { error } = await supabase
      .from("internal_notes")
      .delete()
      .eq("id", noteId);

    if (error) {
      toast.error("Échec de la suppression");
    } else {
      toast.success("Note supprimée");
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    }
    setDeletingNoteId(null);
  };

  const handleStatusChange = async (status: "open" | "pending" | "closed") => {
    if (!isAdmin) {
      toast.error("Seuls les administrateurs peuvent modifier le statut");
      return;
    }
    
    const { error } = await supabase
      .from("conversations")
      .update({ status })
      .eq("id", conversation.id);

    if (error) {
      toast.error("Échec de la mise à jour du statut");
    } else {
      toast.success(`Statut modifié: ${status}`);
      onStatusChange(status);
    }
  };

  const handleServiceChange = async (serviceId: string) => {
    if (!isAdmin) {
      toast.error("Seuls les administrateurs peuvent modifier le service");
      return;
    }
    
    const { error } = await supabase
      .from("conversations")
      .update({ service_id: serviceId })
      .eq("id", conversation.id);

    if (error) {
      toast.error("Échec de la mise à jour du service");
    } else {
      const newService = services.find(s => s.id === serviceId);
      setCurrentService(newService || null);
      toast.success(`Service modifié: ${newService?.name || serviceId}`);
    }
  };

  const handleQuickReply = (content: string) => {
    setNewMessage(content);
    inputRef.current?.focus();
  };

  const handleTogglePin = async (messageId: string, currentlyPinned: boolean) => {
    const { error } = await supabase
      .from("messages")
      .update({
        is_pinned: !currentlyPinned,
        pinned_at: !currentlyPinned ? new Date().toISOString() : null,
        pinned_by: !currentlyPinned ? user?.id : null,
      })
      .eq("id", messageId);

    if (error) {
      toast.error("Erreur lors de l'épinglage");
    } else {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, is_pinned: !currentlyPinned, pinned_at: !currentlyPinned ? new Date().toISOString() : null, pinned_by: !currentlyPinned ? user?.id || null : null }
            : m
        )
      );
      toast.success(currentlyPinned ? "Message désépinglé" : "Message épinglé");
    }
  };

  const handleToggleStar = async (messageId: string, currentlyStarred: boolean) => {
    const { error } = await supabase
      .from("messages")
      .update({
        is_starred: !currentlyStarred,
        starred_at: !currentlyStarred ? new Date().toISOString() : null,
        starred_by: !currentlyStarred ? user?.id : null,
      })
      .eq("id", messageId);

    if (error) {
      toast.error("Erreur");
    } else {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, is_starred: !currentlyStarred, starred_at: !currentlyStarred ? new Date().toISOString() : null, starred_by: !currentlyStarred ? user?.id || null : null }
            : m
        )
      );
    }
  };

  const formatTime = (dateStr: string) => format(new Date(dateStr), "HH:mm");

  const pinnedMessages = messages.filter(m => m.is_pinned && !m.is_deleted);
  const starredMessages = messages.filter(m => m.is_starred && !m.is_deleted);

  if (loading || messagesLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const profile = fullProfile || conversation.user_profile;
  const customerName = `${profile?.first_name || "Unknown"} ${profile?.last_name || "User"}`;
  const isAssignedToMe = conversation.assigned_agent_id === user?.id;
  const isArchived = conversation.is_archived;

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Selection Toolbar */}
      {isSelectionMode && (
        <SelectionToolbar
          selectedCount={selectedMessageIds.size}
          onClearSelection={clearSelection}
          onDeleteClick={() => setDeleteDialogOpen(true)}
        />
      )}

      {/* Header */}
      <header 
        className={`border-b border-border/50 bg-background/80 backdrop-blur-xl sticky top-0 z-10 ${isSelectionMode ? 'mt-14' : ''}`}
        style={{ paddingTop: isSelectionMode ? '0' : 'max(2.5rem, calc(env(safe-area-inset-top, 0px) + 1rem))' }}
      >
        <div className="px-3 py-2.5 flex items-center gap-2">
          {isMobile && onBack && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full shrink-0"
              onClick={onBack}
            >
              <ArrowLeft size={18} />
            </Button>
          )}

          <div className="flex items-center gap-2 flex-1 min-w-0">
            <button 
              onClick={() => setAvatarModalOpen(true)}
              className="relative shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <Avatar className="w-9 h-9">
                {profile?.avatar_url && (
                  <AvatarImage src={profile.avatar_url} alt={customerName} />
                )}
                <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/5 text-primary font-semibold text-xs">
                  {profile?.first_name?.charAt(0) || "?"}{profile?.last_name?.charAt(0) || ""}
                </AvatarFallback>
              </Avatar>
              <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-background ${
                clientOnlineStatus.isOnline ? "bg-emerald-500" : "bg-muted-foreground/50"
              }`} />
            </button>
            
            <div className="flex-1 min-w-0">
              <h2 className="font-medium text-foreground text-sm truncate leading-tight">
                {customerName}
              </h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <OnlineStatus 
                  isOnline={clientOnlineStatus.isOnline} 
                  lastSeenAt={clientOnlineStatus.lastSeenAt}
                  showDot={false}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-0.5 shrink-0">
            {!conversation.assigned_agent_id && !isArchived && (
              <Button 
                variant="default" 
                size="sm" 
                className="h-7 text-xs gap-1 rounded-full px-2.5"
                onClick={onTakeOver}
              >
                <UserPlus size={12} />
                {!isMobile && "Prendre"}
              </Button>
            )}

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" title="Notes">
                  <StickyNote size={16} />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle className="font-serif">Notes internes</SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Ajouter une note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={3}
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={!newNote.trim() || savingNote}
                      size="sm"
                      className="w-full"
                    >
                      {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {notes.map((note) => (
                      <div
                        key={note.id}
                        className="p-3 bg-muted/50 rounded-xl group relative"
                      >
                        <p className="text-sm text-foreground whitespace-pre-wrap pr-6">
                          {note.content}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-[10px] text-muted-foreground">
                            {format(new Date(note.created_at), "d MMM, HH:mm")}
                          </p>
                          {note.agent_id === user?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleDeleteNote(note.id)}
                              disabled={deletingNoteId === note.id}
                            >
                              {deletingNoteId === note.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Trash2 size={12} className="text-destructive" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {notes.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Aucune note
                      </p>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {isMobile ? (
              <QuickRepliesSheet onSelect={handleQuickReply} />
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" title="Réponses rapides">
                    <Zap size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-auto">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    Réponses rapides
                  </div>
                  <DropdownMenuSeparator />
                  {quickReplies.length === 0 ? (
                    <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                      Aucune réponse configurée
                    </div>
                  ) : (
                    Object.entries(
                      quickReplies.reduce((acc, reply) => {
                        const cat = reply.category || "Général";
                        if (!acc[cat]) acc[cat] = [];
                        acc[cat].push(reply);
                        return acc;
                      }, {} as Record<string, typeof quickReplies>)
                    ).map(([category, replies]) => (
                      <div key={category}>
                        <div className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                          {category}
                        </div>
                        {replies.map((reply) => (
                          <DropdownMenuItem
                            key={reply.id}
                            onClick={() => handleQuickReply(reply.content)}
                          >
                            <span className="truncate">{reply.title}</span>
                          </DropdownMenuItem>
                        ))}
                      </div>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                  <MoreHorizontal size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {profile?.phone && (
                  <DropdownMenuItem onClick={() => window.open(`tel:${profile.phone}`)} className="gap-2">
                    <Phone size={14} />
                    Appeler
                  </DropdownMenuItem>
                )}

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="gap-2">
                    <Info size={14} />
                    Infos client
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium">{customerName}</p>
                      {profile?.phone && (
                        <p className="text-xs text-muted-foreground mt-1">{profile.phone}</p>
                      )}
                      {profile?.city && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <MapPin size={10} />
                          {profile.city}
                        </p>
                      )}
                      {currentService && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          {currentService.name}
                        </p>
                      )}
                    </div>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                {starredMessages.length > 0 && (
                  <DropdownMenuItem 
                    onClick={() => {
                      setShowStarredPanel(!showStarredPanel);
                      setShowPinnedPanel(false);
                    }}
                    className="gap-2"
                  >
                    <Star size={14} className="fill-amber-500 text-amber-500" />
                    {starredMessages.length} favori{starredMessages.length > 1 ? "s" : ""}
                  </DropdownMenuItem>
                )}

                {isAdmin && conversation.assigned_agent_id && !isArchived && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setReassignOpen(true)} className="gap-2">
                      <Users size={14} />
                      Transférer
                    </DropdownMenuItem>
                  </>
                )}

                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          conversation.status === "open" ? "bg-emerald-500" : 
                          conversation.status === "pending" ? "bg-amber-500" : "bg-muted-foreground/50"
                        }`} />
                        Statut
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem onClick={() => handleStatusChange("open")} className="gap-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          Ouvert
                          {conversation.status === "open" && <Check size={14} className="ml-auto" />}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleStatusChange("pending")} className="gap-2">
                          <div className="w-2 h-2 rounded-full bg-amber-500" />
                          En attente
                          {conversation.status === "pending" && <Check size={14} className="ml-auto" />}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleStatusChange("closed")} className="gap-2">
                          <div className="w-2 h-2 rounded-full bg-muted-foreground/50" />
                          Fermé
                          {conversation.status === "closed" && <Check size={14} className="ml-auto" />}
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="gap-2">
                        Service
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {services.map((service) => (
                          <DropdownMenuItem 
                            key={service.id} 
                            onClick={() => handleServiceChange(service.id)} 
                            className="gap-2"
                          >
                            {service.name}
                            {conversation.service_id === service.id && <Check size={14} className="ml-auto" />}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Status Bar */}
        <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
          <div className={`
            inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full
            ${conversation.status === "open" ? "bg-emerald-500/10 text-emerald-600" : 
              conversation.status === "pending" ? "bg-amber-500/10 text-amber-600" : 
              "bg-muted text-muted-foreground"}
          `}>
            {conversation.status === "open" && <MessageSquare size={9} />}
            {conversation.status === "pending" && <Clock size={9} />}
            {conversation.status === "closed" && <CheckCircle2 size={9} />}
            {conversation.status === "open" ? "Ouvert" : conversation.status === "pending" ? "En attente" : "Fermé"}
          </div>

          {isAssignedToMe && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
              Assigné à vous
            </span>
          )}

          {!conversation.assigned_agent_id && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-destructive/10 text-destructive">
              Non assigné
            </span>
          )}
        </div>
      </header>

      {/* Pinned Messages Bar */}
      {pinnedMessages.length > 0 && !isSelectionMode && (
        <div className="border-b border-border/50 bg-primary/5 px-3 py-1.5 flex items-center gap-2">
          <Button
            variant={showPinnedPanel ? "default" : "ghost"}
            size="sm"
            className="h-6 text-[11px] gap-1 rounded-full px-2"
            onClick={() => setShowPinnedPanel(!showPinnedPanel)}
          >
            <Pin size={10} />
            {pinnedMessages.length} épinglé{pinnedMessages.length > 1 ? "s" : ""}
            <ChevronUp size={10} className={`transition-transform ${showPinnedPanel ? "rotate-180" : ""}`} />
          </Button>
        </div>
      )}

      {/* Pinned Messages Panel */}
      {showPinnedPanel && pinnedMessages.length > 0 && !isSelectionMode && (
        <div className="border-b border-border/50 bg-primary/5 px-4 py-3 max-h-48 overflow-auto">
          <div className="space-y-2">
            {pinnedMessages.map((msg) => (
              <div key={`pinned-panel-${msg.id}`} className="bg-background rounded-lg px-3 py-2 text-sm flex items-start justify-between gap-2 shadow-sm group">
                <div className="flex-1 min-w-0">
                  <p className="text-foreground line-clamp-2">{msg.content}</p>
                  <span className="text-[10px] text-muted-foreground">
                    {msg.is_from_agent ? "Agent" : "Client"} • {formatTime(msg.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => {
                      scrollToMessage(msg.id);
                      setShowPinnedPanel(false);
                    }}
                  >
                    Aller
                    <ArrowRight size={10} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleTogglePin(msg.id, true)}
                    title="Désépingler"
                  >
                    <PinOff size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Starred Messages Panel */}
      {showStarredPanel && starredMessages.length > 0 && !isSelectionMode && (
        <div className="border-b border-border/50 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 max-h-48 overflow-auto relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6 rounded-full"
            onClick={() => setShowStarredPanel(false)}
          >
            <X size={14} />
          </Button>
          
          <div className="space-y-2 pr-6">
            {starredMessages.map((msg) => (
              <div key={`starred-panel-${msg.id}`} className="bg-background rounded-lg px-3 py-2 text-sm flex items-start justify-between gap-2 shadow-sm group">
                <div className="flex-1 min-w-0">
                  <p className="text-foreground line-clamp-2">{msg.content}</p>
                  <span className="text-[10px] text-muted-foreground">
                    {msg.is_from_agent ? "Agent" : "Client"} • {formatTime(msg.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => {
                      scrollToMessage(msg.id);
                      setShowStarredPanel(false);
                    }}
                  >
                    Aller
                    <ArrowRight size={10} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleToggleStar(msg.id, true)}
                    title="Retirer des favoris"
                  >
                    <Star size={12} className="fill-amber-500 text-amber-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-auto px-4 py-4 bg-background"
      >
        <div className="space-y-2">
          {messages.map((msg) => {
            if (msg.is_system_message) {
              return (
                <SystemMessage 
                  key={msg.id}
                  content={msg.content} 
                  createdAt={msg.created_at} 
                />
              );
            }
            
            if (msg.is_deleted) {
              return (
                <DeletedMessage 
                  key={msg.id}
                  createdAt={msg.created_at}
                  isFromAgent={msg.is_from_agent}
                  deletedForEveryone={true}
                />
              );
            }
            
            return (
              <SelectableMessage
                key={msg.id}
                messageId={msg.id}
                isSelected={isSelected(msg.id)}
                isSelectionMode={isSelectionMode}
                onSelect={selectMessage}
                onToggle={toggleMessageSelection}
                isFromCurrentUser={msg.is_from_agent}
              >
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    <div
                      id={`message-${msg.id}`}
                      className={`flex items-end gap-1.5 ${msg.is_from_agent ? "justify-end" : "justify-start"} group transition-all duration-300`}
                    >
                      {!msg.is_from_agent && (
                        <MessageAvatar 
                          isFromAgent={false} 
                          avatarUrl={fullProfile?.avatar_url}
                          firstName={fullProfile?.first_name}
                          lastName={fullProfile?.last_name}
                          size="xs" 
                        />
                      )}

                      <div className={`hidden sm:flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity self-center ${
                        msg.is_from_agent ? "order-first mr-1" : "order-last ml-1"
                      }`}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleToggleStar(msg.id, msg.is_starred)}
                          title={msg.is_starred ? "Retirer des favoris" : "Ajouter aux favoris"}
                        >
                          <Star size={12} className={msg.is_starred ? "fill-amber-500 text-amber-500" : ""} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleTogglePin(msg.id, msg.is_pinned)}
                          title={msg.is_pinned ? "Désépingler" : "Épingler"}
                        >
                          {msg.is_pinned ? <PinOff size={12} /> : <Pin size={12} />}
                        </Button>
                      </div>

                      {/* Message bubble with sender name above for agent messages */}
                      <div className={`flex flex-col ${msg.is_from_agent ? "items-end" : "items-start"}`}>
                        {/* Sender name - only visible for agent messages in agent view */}
                        {msg.is_from_agent && senderProfiles[msg.sender_id] && (
                          <span className="text-[10px] text-muted-foreground mb-0.5 mr-1">
                            {senderProfiles[msg.sender_id]?.first_name} {senderProfiles[msg.sender_id]?.last_name}
                          </span>
                        )}
                        <div
                          className={`max-w-[80%] md:max-w-[65%] px-4 py-2.5 relative ${
                            msg.is_from_agent ? "chat-bubble-user" : "chat-bubble-agent"
                          } ${msg.is_pinned ? "ring-1 ring-primary/30" : ""} ${msg.is_starred ? "ring-1 ring-amber-400/30" : ""}`}
                        >
                          <div className="absolute top-1 right-1 flex items-center gap-0.5">
                            {msg.is_pinned && <Pin size={10} className="text-primary" />}
                            {msg.is_starred && <Star size={10} className="fill-amber-500 text-amber-500" />}
                          </div>
                          
                          <TruncatedMessage content={msg.content} maxHeight={50} />
                          {msg.attachment_url && msg.attachment_name && msg.attachment_type && (
                            <MessageAttachment
                              url={msg.attachment_url}
                              name={msg.attachment_name}
                              type={msg.attachment_type}
                              isFromAgent={msg.is_from_agent}
                            />
                          )}
                          <div className={`flex items-center gap-1 mt-1 ${msg.is_from_agent ? "justify-end" : "justify-start"}`}>
                            <span className={`text-[10px] ${msg.is_from_agent ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                              {formatTime(msg.created_at)}
                            </span>
                            {msg.is_from_agent && (
                              <MessageStatus
                                createdAt={msg.created_at}
                                deliveredAt={msg.delivered_at}
                                readAt={msg.read_at}
                                isRead={msg.is_read || false}
                                isFromAgent={msg.is_from_agent}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {msg.is_from_agent && (
                        <MessageAvatar 
                          isFromAgent={true} 
                          avatarUrl="/pwa-192x192.png"
                          firstName="C"
                          lastName=""
                          size="xs" 
                        />
                      )}
                    </div>
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem onClick={() => handleToggleStar(msg.id, msg.is_starred)} className="gap-2">
                      <Star size={14} className={msg.is_starred ? "fill-amber-500 text-amber-500" : ""} />
                      {msg.is_starred ? "Retirer des favoris" : "Ajouter aux favoris"}
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => handleTogglePin(msg.id, msg.is_pinned)} className="gap-2">
                      {msg.is_pinned ? <PinOff size={14} /> : <Pin size={14} />}
                      {msg.is_pinned ? "Désépingler" : "Épingler"}
                    </ContextMenuItem>
                    <ContextMenuItem onClick={() => selectMessage(msg.id)} className="gap-2 text-destructive">
                      <Trash2 size={14} />
                      Supprimer
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              </SelectableMessage>
            );
          })}
          
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl px-4 py-2">
                <TypingIndicator typingUsers={typingUsers} />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      <ScrollToBottomButton 
        visible={showScrollButton} 
        onClick={() => scrollToBottom()} 
      />

      {/* Input */}
      <div 
        className="border-t border-border bg-card"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' }}
      >
        {isArchived ? (
          <div className="text-center py-3 px-4 text-sm text-muted-foreground">
            Cette conversation est archivée. Désarchivez-la pour envoyer des messages.
          </div>
        ) : (
          <div className="flex items-end gap-2 px-3 py-2">
            <FileUpload
              disabled={sending}
              onFileUploaded={async (url, name, type) => {
                if (!user) return;
                const { error } = await supabase.from("messages").insert({
                  conversation_id: conversation.id,
                  sender_id: user.id,
                  content: `📎 ${name}`,
                  is_from_agent: true,
                  attachment_url: url,
                  attachment_name: name,
                  attachment_type: type,
                  delivered_at: new Date().toISOString(),
                });
                if (error) {
                  toast.error("Échec de l'envoi du fichier");
                }
              }}
            />
            <div className="flex-1 relative">
              <Textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  sendTyping();
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                }}
                onKeyDown={handleKeyDown}
                onBlur={stopTyping}
                placeholder="Tapez un message..."
                rows={1}
                className="w-full min-h-[44px] max-h-40 py-3 px-4 resize-none rounded-2xl bg-muted/50 border-0 focus-visible:ring-1 focus-visible:ring-primary/30 overflow-y-auto"
                style={{ height: '44px' }}
              />
            </div>
            <Button
              variant="gold"
              size="icon"
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="shrink-0 h-11 w-11 rounded-full"
            >
              {sending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Reassign Dialog */}
      <ReassignDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        conversationId={conversation.id}
        currentAgentId={conversation.assigned_agent_id}
      />

      {/* Avatar Modal */}
      <Dialog open={avatarModalOpen} onOpenChange={setAvatarModalOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-transparent border-none shadow-none">
          <div className="flex flex-col items-center gap-4 p-6">
            <Avatar className="w-48 h-48 ring-4 ring-background shadow-2xl">
              {profile?.avatar_url && (
                <AvatarImage src={profile.avatar_url} alt={customerName} className="object-cover" />
              )}
              <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/5 text-primary font-semibold text-4xl">
                {profile?.first_name?.charAt(0) || "?"}{profile?.last_name?.charAt(0) || ""}
              </AvatarFallback>
            </Avatar>
            <div className="bg-background/95 backdrop-blur-sm rounded-xl px-6 py-3 text-center shadow-lg">
              <h3 className="font-serif font-medium text-lg text-foreground">
                {customerName}
              </h3>
              {profile?.phone && (
                <p className="text-sm text-muted-foreground mt-1">{profile.phone}</p>
              )}
              {(profile?.city || profile?.emirate) && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {[profile?.city, profile?.emirate].filter(Boolean).join(", ")}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Selected Dialog */}
      <DeleteSelectedDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        selectedMessageIds={selectedMessageIds}
        userId={user?.id || ""}
        conversationId={conversation.id}
        canDeleteForEveryone={canDeleteForEveryone()}
        onDeleteComplete={handleDeleteComplete}
        onDeleted={refetch}
      />
    </div>
  );
}
